import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { BaseServices } from './../../kuba.services';
import { HelperService, PdfSetting } from './../../../_services/helper.service';
import { EmployeeContact } from './../models/employeecontact';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CacheHandlerService } from '../../cached.services';
import { EmployeeContactServices } from '../services/employee-admin.service';
import { KubaExportService } from 'src/app/kuba/shared/kuba-exports/kuba-export.service';

@Component({
  selector: 'customer-employee-login',
  templateUrl: 'customer-employee-login.component.html',
  standalone: false
})
export class CustomerEmployeeLoginComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @ViewChild('contactsTable', { static: false }) contactsTable: Table;
  employeeWithlogin: EmployeeContact[];
  selectedEmployees: any = [];
  businesses: { label: string; value: number }[] = [];
  roles: SelectItem[];
  additionalData: any;
  departmentFilter: string = '';
  remoteWindow: any;
  exportInProgress: boolean = false;

  // Pagination variables
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;
  
  // Filter variables
  nameFilter: string = '';
  businessFilter: number = null;
  usernameFilter: string = '';
  mobileFilter: string = '';
  roleFilter: number = null;

  private subscriptions: Subscription[] = [];
  private exportDialogTimeout: any = null;
  
  // Debounce subject for filter changes
  private filterChangeSubject = new Subject<string>();
  
  // Make BaseServices available in template
  public BaseServices = BaseServices;

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private cacheHandlerService: CacheHandlerService,
    private employeeContactServices: EmployeeContactServices,
    private kubaExportService: KubaExportService
  ) {
    this.subscriptions.push(
      this.translate.stream('FILENAME').subscribe(val => {
        this.additionalData = {
          fileName: val.EMPLOYEE_LOGIN_LIST,
          header: 'Employee Login List',
          businessId: BaseServices.BusinessId || 0,
          cultureInfo: BaseServices.userCultureInfo() || 'en-GB',
          columnNames: [
            { title: 'Employee Name', dataKey: 'Name' },
            { title: 'Category', dataKey: 'Category' },
            { title: 'Business Name', dataKey: 'BusinessName' },
            { title: 'User Name', dataKey: 'UserName' },
            { title: 'Mobile', dataKey: 'Mobile' },
            { title: 'Role Name', dataKey: 'RoleName' }
          ]
        };
      })
    );
  }

  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    
    // Setup debounced filter changes
    this.setupFilterDebounce();
    
    // Load initial data
    this.loadEmployees();
    
      // Get businesses from resolver and transform for PrimeNG dropdown
    const businessList = this.route.snapshot.data['businessNames'];
    this.businesses = [{ label: this.translate.instant('SELECT'), value: null }];
    if (businessList && Array.isArray(businessList)) {
      businessList.forEach((b: any) => {
        this.businesses.push({ label: b.Name, value: b.Id });
      });
    }
    // Remove the loading of departments from the resolver

    this.subscriptions.push(
      this.translate.stream('ADD_ROLE').subscribe(val => {
        this.roles = [];
        this.roles.push(
          { label: val.ALL, value: null },
          { label: val.USER, value: 5 },
          { label: val.GUEST, value: 4 },
          { label: val.EDITOR, value: 3 }
        );
      })
    );
    // Set correct values for export
    this.additionalData.businessId = BaseServices.BusinessId || 0;
    this.additionalData.cultureInfo = BaseServices.userCultureInfo() || 'en-GB';
  }
  /**
   * Load employees with pagination
   */
  loadEmployees() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    const filter = {
      PageNumber: this.currentPage,
      PageSize: this.pageSize,
      SortColumn: 'Name',
      SortDirection: 'ASC',
      Name: this.nameFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      UserName: this.usernameFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      RoleId: this.roleFilter || undefined,
      Department: this.departmentFilter || undefined,
      WithUserOnly: true
    };

    this.subscriptions.push(
      this.employeeContactServices.getPagedAdminEmployeeList(filter).subscribe(result => {
        this.employeeWithlogin = result.Items || [];
        this.totalItems = result.TotalCount || 0;
        this.totalPages = result.TotalPages || 0;
        this.isLoading = false;
      },error => {
        console.error('Error loading employees:', error);
        this.isLoading = false;
      })
    );
  }

  /**
   * Handle page change
   */
  onPageChange(event: any) {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.loadEmployees();
  }

  /**
   * Setup debounced filter changes
   */
  private setupFilterDebounce() {
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(
          debounceTime(800) // 800ms debounce for typing
        )
        .subscribe(() => {
          this.currentPage = 1;
          this.loadEmployees();
        })
    );
  }

  /**
   * Trigger debounced filter change
   */
  private triggerFilterChange() {
    this.filterChangeSubject.next('filter');
  }

  /**
   * Handle filter changes
   */
  onFilterChange() {
    this.triggerFilterChange();
  }

  onBusinessChanged(e: any, businessDropdown: any) {
    this.businessFilter = e.value;
    this.triggerFilterChange();
  }
  
  onRoleChanged(e: any, roleDropdown: any) {
    this.roleFilter = e.value;
    this.triggerFilterChange();
  }
  
  // Remove onDepartmentChanged and everything related to departments.
  exportPdf() {
    let employeeIds: any = [];
    if (this.selectedEmployees.length > 0) {
      this.selectedEmployees.forEach((employee: any) => {
        employeeIds.push(employee.Id);
      });

      let pdfSetting = new PdfSetting();
      pdfSetting.date = 'Date:' + HelperService.formatDate(new Date());
      pdfSetting.businessName = '';
      pdfSetting.pageHeader = 'Customer employee details';
      let columns = [
        { title: 'Name', dataKey: 'Name' },
        { title: 'Category', dataKey: 'Category' },
        { title: 'Business Name', dataKey: 'BusinessName' },
        { title: 'UserName', dataKey: 'UserName' },
        { title: 'Mobile', dataKey: 'Mobile' },
        { title: 'RoleName', dataKey: 'RoleName' }
      ];
      HelperService.generatePdf(
        this.selectedEmployees,
        columns,
        'Customer employee list with login',
        pdfSetting,
        'l'
      );
    } else {
      this.notifications.warn('SELECT_ONE_OR_MORE_EMPLOYEES');
    }
  }

  showExportLoader() {
    this.isLoading = true;
    // Start a 5s timer to show the dialog only if still loading
    this.exportDialogTimeout = setTimeout(() => {
      this.exportInProgress = true;
    }, 5000);
  }
  hideExportLoader() {
    this.isLoading = false;
    // Always clear the timer
    if (this.exportDialogTimeout) {
      clearTimeout(this.exportDialogTimeout);
      this.exportDialogTimeout = null;
    }
    this.exportInProgress = false;
  }

  exportEmployeesWithLogin() {
    // The kuba-export component already handles this automatically
    // Just trigger the export through the existing kuba-export component
    console.log('Export triggered through kuba-export component');
  }

  // For kuba-export: export all employees with login with filters
  getAllEmployeesWithLoginForExport = (): Promise<any[]> => {
    const filter = {
      PageNumber: 1,
      PageSize: 100000,
      SortColumn: 'Name',
      SortDirection: 'ASC',
      Name: this.nameFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      UserName: this.usernameFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      RoleId: this.roleFilter || undefined,
      Department: this.departmentFilter || undefined,
      WithUserOnly: true
    };
    return new Promise((resolve, reject) => {
      this.employeeContactServices.getPagedAdminEmployeeList(filter).subscribe({
        next:result => resolve(result.Items || []),
        error:err => reject(err)
      });
    });
  };

  exportToCSV(data: any[], filename: string) {
    if (!data || !data.length) return;
    const header = Object.keys(data[0]);
    const csv = [
      header.join(','), // header row first
      ...data.map(row => header.map(fieldName =>
        JSON.stringify(row[fieldName] ?? '')).join(','))
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  //   Clicking the Employee name opens the employee in a new window
  openEmployeeInNewWindow(employee: any) {
    if (this.remoteWindow) {
      this.remoteWindow.close();
    }
    this.cacheHandlerService.clearCachedData();
    let businessSetting = {
      BusinessName: employee.BusinessName,
      BusinessLogo: employee.BusinessLogo
    };
    sessionStorage.setItem('RModeBusiness', JSON.stringify(businessSetting));
    sessionStorage.setItem('RModeBusinessId', employee.BusinessId);
    let windowOrigin = window.location.origin;
    let windowControl =
      'location=yes,width=' +
      screen.availWidth +
      ',height=' +
      screen.availHeight +
      ',scrollbars=yes,status=yes';

    let baseURL = BaseServices.userBase.BaseUrl;
    this.remoteWindow = window.open(
      windowOrigin +
        '/' +
        baseURL +
        '/app/business/' +
        employee.BusinessId +
        '/employee/edit/' +
        employee.Id,
      'Remote',
      windowControl
    )!;
  }

  //   Clicking the Business name opens the business in a new window
  openBusinessInNewWindow(employee: any) {
    if (this.remoteWindow) {
      this.remoteWindow.close();
    }
    this.cacheHandlerService.clearCachedData();
    let businessSetting = {
      BusinessName: employee.BusinessName,
      BusinessLogo: employee.BusinessLogo
    };
    sessionStorage.setItem('RModeBusiness', JSON.stringify(businessSetting));
    sessionStorage.setItem('RModeBusinessId', employee.BusinessId);
    let windowOrigin = window.location.origin;
    let windowControl =
      'location=yes,width=' +
      screen.availWidth +
      ',height=' +
      screen.availHeight +
      ',scrollbars=yes,status=yes';

    let baseURL = BaseServices.userBase.BaseUrl;
    this.remoteWindow = window.open(
      windowOrigin +
        '/' +
        baseURL +
        '/app/business/' +
        employee.businessId +
        '/dashboard',
      'Remote',
      windowControl
    )!;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
